export type AnalysisMode = 'basic' | 'intermediate' | 'advanced';

export interface AnalysisConfig {
  mode: AnalysisMode;
  minPatternLength: number;
  maxPatternLength: number;
  minFrequency: number;
  
  // Intermediate mode options
  similarityThreshold?: number;
  enableRecencyWeights?: boolean;
  enableLengthWeights?: boolean;
  
  // Advanced mode options
  enableStatisticalValidation?: boolean;
  enableCrossValidation?: boolean;
  enableEnsembleMethods?: boolean;
  confidenceLevel?: number;
  kFoldSplits?: number;
}

export interface PatternSimilarity {
  pattern: string;
  similarity: number;
  distance: number;
  matchType: 'exact' | 'fuzzy';
}

export interface StatisticalMetrics {
  chiSquare?: number;
  pValue?: number;
  confidenceInterval?: [number, number];
  significance?: 'low' | 'medium' | 'high' | 'very_high';
  sampleSize: number;
}

export type PatternCategory = 'HIGH_WIN' | 'MODERATE_WIN' | 'BALANCED' | 'MODERATE_LOSS' | 'HIGH_LOSS';

export interface PatternClassification {
  category: PatternCategory;
  risk: 'very_low' | 'low' | 'medium' | 'high' | 'very_high';
  actionRecommendation: 'strong_buy' | 'buy' | 'neutral' | 'avoid' | 'reverse';
  riskDescription: string;
}

export interface EnhancedPattern {
  pattern: string;
  frequency: number;
  nextWinCount: number;
  nextLossCount: number;
  successRate: number;
  confidenceScore: number;
  
  // Pattern classification
  classification?: PatternClassification;
  
  // Enhanced metrics
  recencyWeight?: number;
  lengthWeight?: number;
  statisticalMetrics?: StatisticalMetrics;
  similarities?: PatternSimilarity[];
  lastSeenIndex?: number;
}

export interface PatternDistribution {
  HIGH_WIN: number;
  MODERATE_WIN: number;
  BALANCED: number;
  MODERATE_LOSS: number;
  HIGH_LOSS: number;
}

export interface AnalysisResult {
  mode: AnalysisMode;
  patterns: EnhancedPattern[];
  totalPatterns: number;
  processingTime: number;
  
  // Pattern classification results
  patternDistribution?: PatternDistribution;
  highRiskPatterns?: EnhancedPattern[];
  opportunityPatterns?: EnhancedPattern[];
  
  // Cached count values for UI display
  highRiskPatternsCount?: number;
  opportunityPatternsCount?: number;
  
  // Mode-specific results
  fuzzyMatches?: number;
  statisticallySignificant?: number;
  crossValidationScore?: number;
  ensembleAccuracy?: number;
}

export interface DataQuality {
  totalTrades: number;
  winTrades: number;
  lossTrades: number;
  winRate: number;
  dataQuality: 'excellent' | 'good' | 'acceptable' | 'poor' | 'very_poor';
  biasWarning?: string;
}

export interface PredictionResult {
  winProbability: number;
  lossProbability: number;
  confidenceLevel: 'very_low' | 'low' | 'medium' | 'high' | 'very_high';
  matchingPatterns: number;
  exactMatches: number;
  prediction: 'CERTAIN' | 'UNCERTAIN' | 'REFUSE';
  reason: string;
  
  // Enhanced prediction metrics
  fuzzyMatches?: number;
  statisticalConfidence?: number;
  ensembleScore?: number;
  anomalyScore?: number;
  dataQuality?: DataQuality;
  
  // Debug information
  debugInfo?: {
    matchedPatterns: Array<{
      pattern: string;
      successRate: number;
      frequency: number;
      confidenceScore: number;
      contribution: number;
    }>;
    rejectedPatterns: Array<{
      pattern: string;
      reason: string;
    }>;
    calculationSummary: {
      totalWeight: number;
      weightedWinRate: number;
      finalAverage: number;
    };
    // Legacy fields for compatibility
    datasetStats?: { totalPatterns: number; winRate: number; totalSamples: number };
    exactMatches?: Array<{ pattern: string; frequency: number; successRate: number; samples: number }>;
    rejectedMatches?: Array<{ pattern: string; reason: string; frequency: number; successRate: number }>;
    qualityScore?: number;
    biasWarning?: string;
  };
}

export interface CrossValidationResult {
  folds: number;
  averageAccuracy: number;
  standardDeviation: number;
  foldResults: Array<{
    fold: number;
    accuracy: number;
    precision: number;
    recall: number;
  }>;
}

export interface ComprehensiveAnalysisResult {
  standardAnalysis: AnalysisResult;
  ensemblePrediction?: PredictionResult;
  crossValidation: CrossValidationResult;
  anomalies: Array<{
    isAnomaly: boolean;
    anomalyScore: number;
    anomalyType: string;
    description: string;
    confidence: number;
    recommendations: string[];
  }>;
  modeComparison: {
    basic: CrossValidationResult;
    intermediate: CrossValidationResult;
    advanced: CrossValidationResult;
  };
}

export const DEFAULT_CONFIGS: Record<AnalysisMode, AnalysisConfig> = {
  basic: {
    mode: 'basic',
    minPatternLength: 3,
    maxPatternLength: 8,
    minFrequency: 2,
  },
  intermediate: {
    mode: 'intermediate',
    minPatternLength: 3,
    maxPatternLength: 10,
    minFrequency: 2,
    similarityThreshold: 0.8,
    enableRecencyWeights: true,
    enableLengthWeights: true,
  },
  advanced: {
    mode: 'advanced',
    minPatternLength: 2,
    maxPatternLength: 12,
    minFrequency: 1,
    similarityThreshold: 0.7,
    enableRecencyWeights: true,
    enableLengthWeights: true,
    enableStatisticalValidation: true,
    enableCrossValidation: true,
    enableEnsembleMethods: true,
    confidenceLevel: 0.95,
    kFoldSplits: 5,
  },
};